import { useCallback, useEffect, useState } from "react"
import { applyActiveVersionListDelete } from "@/lib/characters-api"
import { confirm } from "@/lib/confirm"

type VersionRow = { id: number; active: boolean; created_at: string }

type Args<T extends VersionRow> = {
  open: boolean
  /** Стабильная ссылка (useCallback): смена перезагружает список, пока галерея открыта. */
  list: () => Promise<T[]>
  activate: (versionId: number) => Promise<unknown>
  remove: (versionId: number) => Promise<unknown>
  deleteConfirm: string
  onChanged?: () => void
  /** false — без оптимистичного удаления: после ответа сервера список перечитывается. */
  optimisticDelete?: boolean
}

/** Загрузка, активация и удаление версий для AssetVersionsGallery. */
export function useAssetVersionsController<T extends VersionRow>({
  open,
  list,
  activate,
  remove,
  deleteConfirm,
  onChanged,
  optimisticDelete = true,
}: Args<T>) {
  const [versions, setVersions] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await list()
      setVersions(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [list])

  useEffect(() => {
    if (open) void reload()
  }, [open, reload])

  const handleActivate = async (versionId: number) => {
    setBusyId(versionId)
    setError(null)
    try {
      await activate(versionId)
      setVersions((rows) => rows.map((x) => ({ ...x, active: x.id === versionId })))
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (versionId: number) => {
    if (!(await confirm({ message: deleteConfirm, tone: "danger" }))) return
    setBusyId(versionId)
    setError(null)
    if (!optimisticDelete) {
      try {
        await remove(versionId)
        await reload()
        onChanged?.()
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusyId(null)
      }
      return
    }
    const previous = versions
    setVersions((rows) => applyActiveVersionListDelete(rows, versionId))
    try {
      await remove(versionId)
      onChanged?.()
    } catch (e) {
      setVersions(previous)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  return { versions, loading, busyId, error, setError, reload, handleActivate, handleDelete }
}
